import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { type AuctionStatus } from '@marketplace/contracts';

/**
 * The auction's labels, at the top of its page — operator, 2026-10-01: *"auctions should clearly
 * report the associated labels in their page"*. Unmistakably a **Subasta**, then its terms: state,
 * closing time, length, ceiling. Shared by the client's page and the provider's.
 *
 * `status` and `durationDays` are optional because the provider's view is built from the feed row,
 * which has no length. It has carried the status since `W6-T08`, but the provider's panel is what
 * states it (`BidPanel` in `feed-job.tsx`), so the provider's header still omits it.
 */
export function AuctionHeader({
  locale,
  closesAt,
  maxPriceCents,
  status,
  durationDays,
}: {
  locale: string;
  closesAt: string;
  maxPriceCents: number | null;
  status?: AuctionStatus;
  durationDays?: number;
}): ReactElement {
  const { t } = useTranslation();
  const money = (cents: number): string =>
    (cents / 100).toLocaleString(locale, { style: 'currency', currency: 'EUR' });

  return (
    <section className="mp-section" aria-label={t('auction.label')} data-testid="auction-header">
      <p className="mp-lead">
        <strong>{t('auction.label')}</strong>
        {status === undefined ? null : (
          <span data-testid="auction-header-status"> · {t(`auction.status.${status}`)}</span>
        )}
      </p>
      <ul className="mp-meta">
        <li>
          {t('auction.terms.closes', {
            date: new Date(closesAt).toLocaleString(locale, {
              dateStyle: 'medium',
              timeStyle: 'short',
            }),
          })}
        </li>
        {durationDays === undefined ? null : (
          <li>{t('auction.terms.duration', { count: durationDays })}</li>
        )}
        <li>
          {maxPriceCents === null
            ? t('auction.terms.noMax')
            : t('auction.maxPrice', { amount: money(maxPriceCents) })}
        </li>
      </ul>
    </section>
  );
}

/** Whole days between publishing and the deadline — the preset the client picked. */
export function durationDaysOf(createdAt: string, closesAt: string): number {
  return Math.round((new Date(closesAt).getTime() - new Date(createdAt).getTime()) / 86_400_000);
}
